import * as React from 'react'
import { Plus, Search, Pencil, Trash2, X, Check, Tag as TagIcon, Sparkles, LayoutGrid, List, AlertTriangle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useTags } from '@/hooks/use-tags'
import { useIsMobile } from '@/hooks/use-mobile'
import { ColorPicker } from '@/components/ui/color-picker'
import { PALETTE } from '@/lib/colores-de-etiqueta'

/* ─── Types ─────────────────────────────────────────────── */

interface LocalTag {
  id: string
  name: string
  color: string
  description: string
  createdAt: Date
}

type TagSortField = 'name' | 'color' | 'date'
type SortDir = 'asc' | 'desc'
const MAX_TAG_NAME_LENGTH = 100
const MAX_TAG_DESCRIPTION_LENGTH = 280

function normalizeTagName(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, '_')
}

function validateTagData(data: { name: string; description: string }) {
  const normalizedName = normalizeTagName(data.name)
  const normalizedDescription = data.description.trim()
  const errors = {
    name: '',
    description: '',
  }

  if (!normalizedName) {
    errors.name = 'Tag name is required.'
  } else if (normalizedName.length > MAX_TAG_NAME_LENGTH) {
    errors.name = `Tag name cannot exceed ${MAX_TAG_NAME_LENGTH} characters.`
  }

  if (normalizedDescription.length > MAX_TAG_DESCRIPTION_LENGTH) {
    errors.description = `Description cannot exceed ${MAX_TAG_DESCRIPTION_LENGTH} characters.`
  }

  return {
    normalizedName,
    normalizedDescription,
    errors,
    isValid: !errors.name && !errors.description,
  }
}

/* ─── Color palette ─────────────────────────────────────── */


/* ─── Tag Pill ──────────────────────────────────────────── */

function TagPill({
  name,
  color,
  size = 'md',
  constrained = false,
}: {
  name: string
  color: string
  size?: 'sm' | 'md' | 'lg'
  constrained?: boolean
}) {
  const sizes = {
    sm: 'text-[11px] px-2.5 py-1 gap-2',
    md: 'text-xs px-3 py-1 gap-2',
    lg: 'text-sm px-3.5 py-1.5 gap-2',
  }
  const dotSizes = { sm: 'h-1.5 w-1.5', md: 'h-2 w-2', lg: 'h-2.5 w-2.5' }

  return (
    <span className={cn('inline-flex min-w-0 max-w-full items-center rounded-full font-medium', sizes[size])}
      style={{ backgroundColor: `${color}15`, color }}>
      <span className={cn('rounded-full shrink-0', dotSizes[size])} style={{ backgroundColor: color }} />
      <span className={cn('min-w-0 truncate', constrained && 'max-w-[180px]')} title={name}>
        {name}
      </span>
    </span>
  )
}

/* ─── Custom Color Picker Styles ─────────────────────────── */


/* ─── Color Picker Row ──────────────────────────────────── */


/* ─── Dialog ────────────────────────────────────────────── */

function TagDialog({ initial, onSubmit, onCancel, isEdit }: {
  initial: { name: string; color: string; description: string }
  onSubmit: (data: { name: string; color: string; description: string }) => void
  onCancel: () => void
  isEdit: boolean
}) {
  const isMobile = useIsMobile()
  const [form, setForm] = React.useState(initial)
  const [attemptedSubmit, setAttemptedSubmit] = React.useState(false)
  const nameRef = React.useRef<HTMLInputElement>(null)
  const { t } = useTranslation()

  React.useEffect(() => { setTimeout(() => nameRef.current?.focus(), 100) }, [])
  React.useEffect(() => { setForm(initial); setAttemptedSubmit(false) }, [initial])

  const validation = React.useMemo(
    () => validateTagData({ name: form.name, description: form.description }),
    [form.name, form.description]
  )
  const canSubmit = validation.isValid

  const handleSubmit = () => {
    setAttemptedSubmit(true)
    if (!canSubmit) return
    onSubmit(form)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    }
    if (e.key === 'Escape') onCancel()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm animate-in fade-in duration-150" onClick={onCancel} />
      <div className={cn(
        "relative z-10 mx-4 w-full max-w-md rounded-lg border bg-background p-6 shadow-md animate-in fade-in zoom-in-95 duration-200",
        isMobile ? "max-h-[min(85dvh,720px)] overflow-y-auto" : "max-h-none overflow-visible"
      )}
        onKeyDown={handleKeyDown}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-semibold">{isEdit ? 'Edit Tag' : 'New Tag'}</h3>
          <Button variant="ghost" size="icon-xs" onClick={onCancel} aria-label="Cerrar">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="grid gap-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{t('tags_manager.name')}</label>
            <Input
              ref={nameRef}
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value.slice(0, MAX_TAG_NAME_LENGTH) }))}
              placeholder="e.g. vip, urgent, marbella..."
              className="h-10 text-sm"
              maxLength={MAX_TAG_NAME_LENGTH}
            />
            <div className="mt-1 flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">
                Use up to {MAX_TAG_NAME_LENGTH} characters.
              </span>
              <span className="text-[11px] text-muted-foreground">
                {form.name.length}/{MAX_TAG_NAME_LENGTH}
              </span>
            </div>
            {attemptedSubmit && validation.errors.name && (
              <p className="mt-1 text-[11px] text-destructive">{validation.errors.name}</p>
            )}
          </div>
          <div>
            <label className="mb-2.5 block text-xs font-medium text-muted-foreground">{t('tags_manager.color')}</label>
            <ColorPicker value={form.color} onChange={hex => setForm(f => ({ ...f, color: hex }))} />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{t('tags_manager.description')}</label>
            <Input
              value={form.description}
              onChange={e =>
                setForm(f => ({ ...f, description: e.target.value.slice(0, MAX_TAG_DESCRIPTION_LENGTH) }))
              }
              placeholder="Brief description of this tag..."
              className="h-10 text-sm"
              maxLength={MAX_TAG_DESCRIPTION_LENGTH}
            />
            <div className="mt-1 flex items-center justify-between">
              <span className="text-[11px] text-muted-foreground">
                Use up to {MAX_TAG_DESCRIPTION_LENGTH} characters.
              </span>
              <span className="text-[11px] text-muted-foreground">
                {form.description.length}/{MAX_TAG_DESCRIPTION_LENGTH}
              </span>
            </div>
            {attemptedSubmit && validation.errors.description && (
              <p className="mt-1 text-[11px] text-destructive">{validation.errors.description}</p>
            )}
          </div>
          {form.name.trim() && (
            <div className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
              <span>Preview:</span>
              <div className="min-w-0 flex-1">
                <TagPill name={normalizeTagName(form.name)} color={form.color} size="md" constrained />
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-border">
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
          <Button type="button" size="sm" disabled={!canSubmit} onClick={handleSubmit} className="gap-2">
            {isEdit ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {isEdit ? 'Save Changes' : 'Create Tag'}
          </Button>
        </div>
      </div>
    </div>
  )
}

/* ─── Tag Card ──────────────────────────────────────────── */

function TagCard({ tag, onEdit, onDelete, isNew, isSelected, onToggleSelect }: {
  tag: LocalTag; onEdit: () => void; onDelete: () => void; isNew?: boolean
  isSelected?: boolean; onToggleSelect?: (id: string, multi: boolean) => void
}) {
  return (
    <div
      data-tag-card={tag.id}
      className={cn(
        'group relative rounded-lg border p-4 transition-all hover:shadow-sm',
        isNew && 'animate-in fade-in zoom-in-95 duration-400',
        isSelected && 'ring-2 ring-primary bg-primary/5',
      )}
      style={{
        backgroundColor: isSelected ? undefined : `${tag.color}08`,
        borderColor: isSelected ? undefined : `${tag.color}18`,
      }}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return
        if (e.metaKey || e.ctrlKey || e.shiftKey) {
          e.preventDefault()
          onToggleSelect?.(tag.id, true)
        }
      }}
    >
      {isNew && (
        <div className="absolute inset-0 rounded-lg animate-pulse pointer-events-none"
          style={{ boxShadow: `0 0 24px 4px ${tag.color}20, inset 0 0 24px 4px ${tag.color}06` }} />
      )}
      <div className="absolute right-2.5 top-2.5 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="icon-xs" onClick={onEdit} aria-label="Edit">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon-xs" onClick={onDelete} aria-label="Delete" className="hover:text-destructive">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div className="mb-3 flex items-center gap-2">
        <TagPill name={tag.name} color={tag.color} size="lg" constrained />
        {isNew && <Sparkles className="h-3.5 w-3.5 text-warning animate-pulse" />}
      </div>
      {tag.description && (
        <p className="text-xs text-muted-foreground leading-relaxed break-words line-clamp-2">{tag.description}</p>
      )}
    </div>
  )
}

/* ─── Tag Row (list view) ────────────────────────────────── */

function TagRow({ tag, onEdit, onDelete, isNew, isSelected, onToggleSelect, sortField, sortDir, isMobile }: {
  tag: LocalTag; onEdit: () => void; onDelete: () => void; isNew?: boolean
  isSelected?: boolean; onToggleSelect?: (id: string, multi: boolean) => void
  sortField?: TagSortField; sortDir?: SortDir
  isMobile?: boolean
}) {
  return (
    <div
      data-tag-card={tag.id}
      data-sort-state={`${sortField ?? 'name'}_${sortDir ?? 'asc'}`}
      className={cn(
        'group relative flex w-full min-w-0 max-w-full items-start rounded-lg border px-4 py-3 transition-all hover:shadow-sm',
        isMobile ? 'flex-wrap gap-2' : 'gap-4',
        isNew && 'animate-in fade-in slide-in-from-left-2 duration-400',
        isSelected && 'ring-2 ring-primary bg-primary/5',
      )}
      style={{
        backgroundColor: isSelected ? undefined : `${tag.color}08`,
        borderColor: isSelected ? undefined : `${tag.color}18`,
      }}
      onClick={(e) => {
        if ((e.target as HTMLElement).closest('button')) return
        if (e.metaKey || e.ctrlKey || e.shiftKey) {
          e.preventDefault()
          onToggleSelect?.(tag.id, true)
        }
      }}
    >
      {isNew && (
        <div className="absolute inset-0 rounded-lg animate-pulse pointer-events-none"
          style={{ boxShadow: `0 0 24px 4px ${tag.color}20` }} />
      )}
      <div className={cn("flex min-w-0 shrink items-center gap-2", isMobile ? "max-w-full flex-1" : "max-w-[220px]")}>
        <TagPill name={tag.name} color={tag.color} size="md" constrained />
        {isNew && <Sparkles className="h-3.5 w-3.5 text-warning animate-pulse" />}
      </div>
      <p className={cn("min-w-0 max-w-full overflow-hidden whitespace-normal break-words text-xs text-muted-foreground line-clamp-2", isMobile ? "basis-full" : "flex-1")}>
        {tag.description || <span className="italic text-muted-foreground/40">No description</span>}
      </p>
      {/* Date column visible in list */}
      <span className="text-[11px] text-muted-foreground/50 shrink-0 hidden md:block w-24 text-right">
        {tag.createdAt.toLocaleDateString()}
      </span>
      <div className="ml-auto flex shrink-0 items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
        <Button variant="ghost" size="icon-xs" onClick={onEdit} aria-label="Edit">
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="icon-xs" onClick={onDelete} aria-label="Delete" className="hover:text-destructive">
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )
}

/* ─── Main Component ────────────────────────────────────── */

export function TagsManager() {
  const isMobile = useIsMobile()
  const [search, setSearch] = React.useState('')
  const [mobileSearchOpen, setMobileSearchOpen] = React.useState(false)
  const [view, setView] = React.useState<'grid' | 'list'>('grid')
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [editingTag, setEditingTag] = React.useState<LocalTag | null>(null)
  const [justCreatedIds, setJustCreatedIds] = React.useState<Set<string>>(new Set())
  const [hasInitialLoadCompleted, setHasInitialLoadCompleted] = React.useState(false)

  /* ── Sorting & Filtering ────────────────────────────────── */
  const [sortField, setSortField] = React.useState<TagSortField>('name')
  const [sortDir, setSortDir] = React.useState<SortDir>('asc')
  const [colorFilter, setColorFilter] = React.useState<string>('all')

  const sortFieldToBackend = React.useMemo(() => {
    if (sortField === 'date') return 'created_at'
    return sortField
  }, [sortField])

  const { tags: apiTags, loading, error, loadTags, createTag, updateTag, deleteTag } = useTags({
    autoLoad: false,
    search: '',
    orderBy: 'name',
    order: 'asc',
  })

  React.useEffect(() => {
    let isCancelled = false
    const timeout = window.setTimeout(() => {
      void (async () => {
        try {
          await loadTags({
            search: search.trim(),
            orderBy: sortFieldToBackend,
            order: sortDir,
          })
        } finally {
          if (!isCancelled) {
            setHasInitialLoadCompleted(true)
          }
        }
      })()
    }, 250)

    return () => {
      isCancelled = true
      window.clearTimeout(timeout)
    }
  }, [search, sortFieldToBackend, sortDir, loadTags])

  const tags = React.useMemo<LocalTag[]>(
    () =>
      apiTags.map(tag => ({
        id: tag.id,
        name: tag.name,
        color: tag.color,
        description: tag.description || '',
        createdAt: tag.created_at ? new Date(tag.created_at) : new Date(),
      })),
    [apiTags],
  )

  const filtered = React.useMemo(() => {
    let list = [...tags]
    // Color filter
    if (colorFilter !== 'all') {
      list = list.filter(tag => tag.color.toLowerCase() === colorFilter.toLowerCase())
    }
    return list
  }, [tags, colorFilter])

  /* ── Selection ──────────────────────────────────────────── */
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set())

  const toggleSelect = React.useCallback((id: string, multi: boolean) => {
    setSelectedIds(prev => {
      const next = multi ? new Set(prev) : new Set<string>()
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }, [])

  const clearSelection = React.useCallback(() => setSelectedIds(new Set()), [])

  /* ── Rubber-band selection ─────────────────────────────── */
  const contentRef = React.useRef<HTMLDivElement>(null)
  const [rubberBand, setRubberBand] = React.useState<{ startX: number; startY: number; x: number; y: number } | null>(null)
  const rbStartRef = React.useRef<{ x: number; y: number; scrollTop: number } | null>(null)

  const handleRbMouseDown = React.useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-tag-card]')) return
    if ((e.target as HTMLElement).closest('button')) return
    if ((e.target as HTMLElement).closest('input')) return
    if ((e.target as HTMLElement).closest('select')) return
    if (e.button !== 0) return
    const el = contentRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top + el.scrollTop
    rbStartRef.current = { x, y, scrollTop: el.scrollTop }
    if (!e.shiftKey && !e.metaKey && !e.ctrlKey) clearSelection()
  }, [clearSelection])

  const handleRbMouseMove = React.useCallback((e: React.MouseEvent) => {
    const start = rbStartRef.current
    const el = contentRef.current
    if (!start || !el) return
    const rect = el.getBoundingClientRect()
    const curX = e.clientX - rect.left
    const curY = e.clientY - rect.top + el.scrollTop
    if (Math.abs(curX - start.x) < 5 && Math.abs(curY - start.y) < 5 && !rubberBand) return

    // Rubber band coords relative to the scrolling container's content
    const rbLeft = Math.min(start.x, curX)
    const rbTop = Math.min(start.y, curY)
    const rbRight = Math.max(start.x, curX)
    const rbBottom = Math.max(start.y, curY)

    // For the visual rectangle, convert to viewport-relative within the container
    const visTop = rbTop - el.scrollTop
    const visBottom = rbBottom - el.scrollTop
    setRubberBand({ startX: rbLeft, startY: visTop, x: rbRight, y: visBottom })

    // Hit-test: compare rubber band (viewport coords) against card bounding rects
    const rbScreenLeft = rbLeft + rect.left
    const rbScreenRight = rbRight + rect.left
    const rbScreenTop = rbTop - el.scrollTop + rect.top
    const rbScreenBottom = rbBottom - el.scrollTop + rect.top

    const ids = new Set<string>()
    el.querySelectorAll('[data-tag-card]').forEach(card => {
      const cr = card.getBoundingClientRect()
      const overlap = !(cr.right < rbScreenLeft || cr.left > rbScreenRight || cr.bottom < rbScreenTop || cr.top > rbScreenBottom)
      if (overlap) {
        const id = (card as HTMLElement).getAttribute('data-tag-card')
        if (id) ids.add(id)
      }
    })
    setSelectedIds(ids)
  }, [rubberBand])

  const handleRbMouseUp = React.useCallback(() => {
    rbStartRef.current = null
    setRubberBand(null)
  }, [])

  // Ensure rubber-band stops even if mouse is released outside the container
  React.useEffect(() => {
    const onUp = () => {
      if (rbStartRef.current) {
        rbStartRef.current = null
        setRubberBand(null)
      }
    }
    window.addEventListener('mouseup', onUp)
    return () => window.removeEventListener('mouseup', onUp)
  }, [])

  /* ── CRUD ──────────────────────────────────────────────── */

  const handleCreate = async (data: { name: string; color: string; description: string }) => {
    const validation = validateTagData(data)
    if (!validation.isValid) return

    const createdTag = await createTag({
      name: validation.normalizedName,
      color: data.color,
      description: validation.normalizedDescription,
      tag_type: 'universal',
    })
    await loadTags({ search: search.trim(), orderBy: sortFieldToBackend, order: sortDir })
    setDialogOpen(false)
    setEditingTag(null)
    if (createdTag?.id) {
      setJustCreatedIds(prev => new Set(prev).add(createdTag.id))
      setTimeout(() => {
        setJustCreatedIds(prev => {
          const next = new Set(prev)
          next.delete(createdTag.id)
          return next
        })
      }, 2500)
    }
  }

  const handleUpdate = async (data: { name: string; color: string; description: string }) => {
    if (!editingTag) return
    const validation = validateTagData(data)
    if (!validation.isValid) return

    await updateTag(editingTag.id, {
      name: validation.normalizedName,
      color: data.color,
      description: validation.normalizedDescription,
    })
    await loadTags({ search: search.trim(), orderBy: sortFieldToBackend, order: sortDir })
    setEditingTag(null)
    setDialogOpen(false)
  }

  /* ── Delete confirmation ──────────────────────────────── */
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false)
  const [pendingDeleteIds, setPendingDeleteIds] = React.useState<string[]>([])

  const requestDelete = (ids: string[]) => {
    setPendingDeleteIds(ids)
    setDeleteConfirmOpen(true)
  }

  const confirmDelete = async () => {
    await Promise.all(pendingDeleteIds.map((id) => deleteTag(id)))
    await loadTags({ search: search.trim(), orderBy: sortFieldToBackend, order: sortDir })
    setPendingDeleteIds([])
    setDeleteConfirmOpen(false)
    clearSelection()
  }

  const pendingDeleteTags = pendingDeleteIds.map(id => tags.find(t => t.id === id)).filter(Boolean) as LocalTag[]

  const openCreate = () => { setEditingTag(null); setDialogOpen(true) }
  const openEdit = (tag: LocalTag) => { setEditingTag(tag); setDialogOpen(true) }
  const closeDialog = () => { setEditingTag(null); setDialogOpen(false) }

  // Get unique colors used in tags for the color filter dropdown
  const usedColors = React.useMemo(() => {
    const colorSet = new Set(tags.map(t => t.color.toLowerCase()))
    return PALETTE.filter(p => colorSet.has(p.hex.toLowerCase()))
  }, [tags])

  const totalTags = filtered.length
  const showInitialSkeleton = !hasInitialLoadCompleted || (loading && tags.length === 0)
  const showRefreshingState = hasInitialLoadCompleted && loading && tags.length > 0

  return (
    <div className="flex h-full flex-col">

      {/* ── Toolbar ──────────────────────────────────────── */}
      <div className={cn("px-6 pt-6 pb-3", isMobile ? "" : "")}>
        {isMobile ? (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1">
              <Button size="icon-xs" onClick={openCreate}>
                <Plus className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon-xs"
                className="shrink-0"
                onClick={() => {
                  setMobileSearchOpen((open) => !open)
                  if (mobileSearchOpen) setSearch('')
                }}
                aria-label="Search tags"
              >
                <Search className="h-3.5 w-3.5" />
              </Button>
            </div>
            <div className="flex items-center gap-1">
              <div className="flex items-center rounded-lg border bg-muted/30 p-0.5">
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label="Grid view"
                  className={cn('rounded-md', view === 'grid' && 'bg-background shadow-sm text-foreground')}
                  onClick={() => setView('grid')}
                >
                  <LayoutGrid className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-xs"
                  aria-label="List view"
                  className={cn('rounded-md', view === 'list' && 'bg-background shadow-sm text-foreground')}
                  onClick={() => setView('list')}
                >
                  <List className="h-3 w-3" />
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative max-w-xs min-w-[180px] flex-1">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/50" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search tags..."
                className="h-9 pl-9 text-sm"
              />
              {search && (
                <Button variant="ghost" size="icon-xs" onClick={() => setSearch('')} aria-label="Limpiar" className="absolute right-2 top-1/2 -translate-y-1/2">
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>

            <div className="flex-1" />

            {/* Color filter */}
            <Select value={colorFilter} onValueChange={setColorFilter}>
              <SelectTrigger className="h-8 w-auto min-w-[6rem] gap-2 rounded-full border-dashed text-xs text-muted-foreground hover:text-foreground transition-colors">
                <SelectValue placeholder="All colors" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All colors</SelectItem>
                {usedColors.map(c => (
                  <SelectItem key={c.hex} value={c.hex}>
                    <span className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: c.hex }} />
                      {c.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={`${sortField}_${sortDir}`} onValueChange={(v) => { const [f, d] = v.split('_') as [TagSortField, SortDir]; setSortField(f); setSortDir(d) }}>
              <SelectTrigger className="h-8 w-auto min-w-[8rem] gap-2 rounded-full border-dashed text-xs text-muted-foreground hover:text-foreground transition-colors">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name_asc">Name (A–Z)</SelectItem>
                <SelectItem value="name_desc">Name (Z–A)</SelectItem>
                <SelectItem value="color_asc">Color</SelectItem>
                <SelectItem value="date_desc">Newest first</SelectItem>
                <SelectItem value="date_asc">Oldest first</SelectItem>
              </SelectContent>
            </Select>

            {showRefreshingState && (
              <span className="hidden items-center gap-2 text-xs text-muted-foreground sm:inline-flex">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Updating results...
              </span>
            )}

            {totalTags > 0 && (
              <span className="hidden text-xs text-muted-foreground/50 sm:block">
                {totalTags} tag{totalTags !== 1 ? 's' : ''}
              </span>
            )}

            {/* View switcher */}
            <div className="flex items-center rounded-lg bg-muted/50 p-0.5">
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Grid view"
                className={cn('rounded-md', view === 'grid' && 'bg-background shadow-sm text-foreground')}
                onClick={() => setView('grid')}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="List view"
                className={cn('rounded-md', view === 'list' && 'bg-background shadow-sm text-foreground')}
                onClick={() => setView('list')}
              >
                <List className="h-3.5 w-3.5" />
              </Button>
            </div>

            <Button size="sm" onClick={openCreate} className="gap-2">
              <Plus className="h-3.5 w-3.5" />
              New Tag
            </Button>
          </div>
        )}
      </div>

      {isMobile && mobileSearchOpen && (
        <div className="px-6 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground/50" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search tags..."
              className="h-9 pl-9 text-sm"
              autoFocus
            />
            {search && (
              <Button variant="ghost" size="icon-xs" onClick={() => setSearch('')} aria-label="Limpiar" className="absolute right-2 top-1/2 -translate-y-1/2">
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        </div>
      )}

      {/* ── Dialog ───────────────────────────────────────── */}
      {dialogOpen && (
        <TagDialog
          key={editingTag?.id || 'new'}
          initial={editingTag
            ? { name: editingTag.name, color: editingTag.color, description: editingTag.description }
            : { name: '', color: '#1238c4', description: '' }
          }
          onSubmit={editingTag ? handleUpdate : handleCreate}
          onCancel={closeDialog}
          isEdit={!!editingTag}
        />
      )}

      {/* ── Scrollable content area with rubber-band selection ── */}
      <div
        ref={contentRef}
        className="flex-1 min-h-0 overflow-auto relative select-none px-6 pb-6 pt-1"
        onMouseDown={handleRbMouseDown}
        onMouseMove={handleRbMouseMove}
        onMouseUp={handleRbMouseUp}
      >
        {showInitialSkeleton && (
          <div className="py-4">
            <div className={cn(view === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 p-2' : 'flex flex-col gap-2 p-2')}>
              {Array.from({ length: view === 'grid' ? 8 : 6 }).map((_, index) => (
                <div key={`tags-skeleton-${index}`} className="rounded-lg border bg-card/40 p-4">
                  <div className="mb-3 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-6 w-6 rounded-full" />
                      <Skeleton className="h-4 w-28" />
                    </div>
                    <Skeleton className="h-7 w-7 rounded-md" />
                  </div>
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="mt-2 h-3 w-2/3" />
                  <div className="mt-4 flex items-center justify-between">
                    <Skeleton className="h-5 w-20 rounded-full" />
                    <Skeleton className="h-3 w-14" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showRefreshingState && (
          <div className="pointer-events-none absolute right-8 top-4 z-10">
            <div className="inline-flex items-center gap-2 rounded-full border bg-background px-3 py-1.5 text-xs text-muted-foreground shadow-sm">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Updating results...
            </div>
          </div>
        )}

        {hasInitialLoadCompleted && !loading && error && (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-sm text-destructive">Failed to load tags</p>
            <p className="mt-1 text-xs text-muted-foreground">{error}</p>
          </div>
        )}

        {/* Empty states */}
        {hasInitialLoadCompleted && !error && filtered.length === 0 && !search && colorFilter === 'all' && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-muted/50">
              <TagIcon className="h-7 w-7 text-muted-foreground/40" />
            </div>
            <h3 className="mt-4 text-sm font-medium">No tags yet</h3>
            <p className="mt-1.5 text-xs text-muted-foreground text-center max-w-[240px]">
              Create tags to organize documents, URLs, and leads. Descriptions guide semantic matching; they do not create hard assignment rules.
            </p>
            <Button size="sm" className="mt-5 gap-2" onClick={openCreate}>
              <Plus className="h-3.5 w-3.5" /> Create your first tag
            </Button>
          </div>
        )}

        {hasInitialLoadCompleted && !error && filtered.length === 0 && (search || colorFilter !== 'all') && (
          <div className="flex flex-col items-center justify-center py-12">
            <p className="text-sm text-muted-foreground">
              {search ? <>No tags matching &ldquo;{search}&rdquo;</> : 'No tags with this color'}
            </p>
          </div>
        )}

        {/* Tag Grid */}
        {hasInitialLoadCompleted && !error && filtered.length > 0 && view === 'grid' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2 p-2">
            {filtered.map(tag => (
              <TagCard
                key={tag.id}
                tag={tag}
                onEdit={() => openEdit(tag)}
                onDelete={() => requestDelete([tag.id])}
                isNew={justCreatedIds.has(tag.id)}
                isSelected={selectedIds.has(tag.id)}
                onToggleSelect={toggleSelect}
              />
            ))}
          </div>
        )}

        {/* Tag List */}
        {hasInitialLoadCompleted && !error && filtered.length > 0 && view === 'list' && (
          <div className="flex flex-col gap-2 p-2">
            {filtered.map(tag => (
              <TagRow
                key={tag.id}
                tag={tag}
                onEdit={() => openEdit(tag)}
                onDelete={() => requestDelete([tag.id])}
                isNew={justCreatedIds.has(tag.id)}
                isSelected={selectedIds.has(tag.id)}
                onToggleSelect={toggleSelect}
                sortField={sortField}
                sortDir={sortDir}
                isMobile={isMobile}
              />
            ))}
          </div>
        )}

        {/* Rubber-band rectangle */}
        {rubberBand && (
          <div
            className="absolute border-2 border-primary/50 bg-primary/10 rounded-md pointer-events-none z-20"
            style={{
              left: rubberBand.startX,
              top: rubberBand.startY,
              width: rubberBand.x - rubberBand.startX,
              height: rubberBand.y - rubberBand.startY,
            }}
          />
        )}
      </div>

      {/* ── Floating bulk action bar ─────────────────────── */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-[calc(1.5rem+env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-30 flex max-w-[calc(100vw-1rem)] flex-wrap items-center justify-center gap-2 rounded-lg border bg-background shadow-md px-5 py-3 animate-in fade-in slide-in-from-bottom-4 duration-200">
          <span className="text-sm font-medium">{selectedIds.size} selected</span>
          <div className="h-5 w-px bg-border/60" />
          <Button
            variant="destructive"
            size="xs"
            className="gap-2"
            onClick={() => requestDelete(Array.from(selectedIds))}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={clearSelection}
            aria-label="Limpiar seleccion"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* ── Delete Confirmation Dialog ───────────────────── */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {pendingDeleteIds.length > 1
                ? `Delete ${pendingDeleteIds.length} tags?`
                : 'Delete tag?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                {pendingDeleteTags.length === 1 ? (
                  <span>
                    Are you sure you want to delete <strong className="text-foreground">{pendingDeleteTags[0].name}</strong>? This action cannot be undone.
                  </span>
                ) : pendingDeleteTags.length > 1 ? (
                  <div className="space-y-2">
                    <span>Are you sure you want to delete these {pendingDeleteTags.length} tags? This action cannot be undone.</span>
                    <div className="flex flex-wrap gap-2 pt-1">
                      {pendingDeleteTags.slice(0, 10).map(tag => (
                        <TagPill key={tag.id} name={tag.name} color={tag.color} size="sm" />
                      ))}
                      {pendingDeleteTags.length > 10 && (
                        <span className="text-xs text-muted-foreground self-center">+{pendingDeleteTags.length - 10} more</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <span>Are you sure? This action cannot be undone.</span>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive text-white">
              Delete{pendingDeleteIds.length > 1 ? ` (${pendingDeleteIds.length})` : ''}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
